import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowRight, 
  Briefcase, 
  FileText, 
  TrendingUp, 
  CheckCircle2, 
  Shield, 
  Lock, 
  Zap,
  Users,
  MessageSquare,
  Star,
  Gift,
  Globe
} from 'lucide-react';
import { UpliftLogo, UpliftLogoWhite, UpliftShield } from '../components/UpliftLogo';
import { trackBetaSignup, trackCTAClick } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UpliftHome = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);

  // Fetch real subscriber count
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscribers/count`);
        if (res.ok) {
          const data = await res.json();
          setSubscriberCount(data.count);
        }
      } catch (error) {
        // Non-critical: subscriber count fetch failed, use default
        console.debug('Subscriber count fetch failed:', error.message);
      }
    };
    fetchCount();
  }, []);

  const handleBetaSignup = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        toast.success("You're in! Check your email for early access details.");
        trackBetaSignup(email);
        setEmail('');
        setSubscriberCount(prev => prev + 1);
      } else {
        const data = await res.json();
        if (data.detail === 'Email already subscribed') {
          toast.info("You're already on the list!");
        } else {
          toast.error('Something went wrong');
        }
      }
    } catch (error) {
      toast.error('Failed to sign up');
    } finally {
      setIsSubmitting(false);
    }
  };

  const products = [
    {
      name: 'CareerBoost',
      tagline: 'Land Your Dream Job',
      description: 'AI resume builder, job tracker, and content repurposer for job seekers.',
      icon: <Briefcase className="w-8 h-8" />,
      color: 'from-indigo-500 to-indigo-700',
      link: '/careerboost',
      features: ['AI Resume Builder', 'Job Tracker', 'Content Repurposer'],
    },
    {
      name: 'BizPlanAI',
      tagline: 'Launch Your Business',
      description: 'Professional business plans in minutes. Perfect for loans and investors.',
      icon: <FileText className="w-8 h-8" />,
      color: 'from-emerald-500 to-emerald-700',
      link: '/bizplanai',
      features: ['AI Business Plans', 'Financial Projections', 'Investor-Ready'],
    },
    {
      name: 'CreditBoost',
      tagline: 'Fix Your Credit',
      description: 'Personalized action plans and dispute letters to boost your score.',
      icon: <TrendingUp className="w-8 h-8" />,
      color: 'from-blue-500 to-blue-700',
      link: '/creditboost',
      features: ['Score Simulator', 'Action Plans', 'Dispute Letters'],
    },
  ];

  const trustBadges = [
    { icon: <Zap className="w-5 h-5" />, text: 'AI-Powered' },
    { icon: <Lock className="w-5 h-5" />, text: 'Secure Payments' },
    { icon: <Shield className="w-5 h-5" />, text: 'Data Protected' },
    { icon: <CheckCircle2 className="w-5 h-5" />, text: 'Stripe Verified' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" data-testid="uplift-logo">
              <UpliftLogoWhite size="sm" showText={true} />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/es" className="flex items-center gap-1 text-slate-400 hover:text-white text-sm">
                <Globe className="w-4 h-4" />
                ES
              </Link>
              <Link to="/login">
                <Button variant="ghost" className="text-slate-300 hover:text-white">Log in</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          {/* Large Shield Logo */}
          <div className="flex justify-center mb-8">
            <UpliftShield size={120} color="#6366F1" />
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Uplift Your Life
          </h1>
          <p className="text-xl md:text-2xl text-slate-400 mb-4 max-w-3xl mx-auto">
            Three powerful tools. One mission.
          </p>
          <p className="text-lg text-slate-500 mb-8 max-w-2xl mx-auto">
            Career success. Business growth. Financial freedom. We help you rise in every area that matters.
          </p>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {trustBadges.map((badge, index) => (
              <div 
                key={index}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-full border border-slate-700"
              >
                <span className="text-indigo-400">{badge.icon}</span>
                <span className="text-slate-300 text-sm">{badge.text}</span>
              </div>
            ))}
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-lg px-8">
                Start Your Journey
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="#products">
              <Button size="lg" variant="outline" className="text-lg px-8 border-slate-700 text-slate-300 hover:bg-slate-800">
                Explore Products
              </Button>
            </Link>
          </div>

          {/* Social Proof Counter */}
          {subscriberCount > 0 && (
            <div className="mt-8 flex items-center justify-center gap-2 text-slate-400">
              <Users className="w-4 h-4" />
              <span>Join {subscriberCount}+ early adopters already signed up</span>
            </div>
          )}
        </div>
      </section>

      {/* Founder Story Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-8 md:p-12">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-white">R</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Robert Elder III
                  </h3>
                  <p className="text-indigo-400">Founder & CEO</p>
                </div>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="text-lg italic">
                  "I applied to over 200 jobs and got barely any responses. That's when I realized my resume was the problem, not my skills."
                </p>
                <p>
                  I built CareerBoost to fix that. Then I saw entrepreneurs struggling to get loans because they couldn't afford $2,000 business plan consultants - so I built BizPlanAI. And when friends asked how to fix their credit, I built CreditBoost.
                </p>
                <p>
                  <strong className="text-white">Uplift isn't just a business - it's my mission to make success accessible to everyone, not just those who can afford expensive consultants.</strong>
                </p>
              </div>
              <div className="mt-6 flex items-center gap-4">
                <div className="flex">
                  {[1,2,3,4,5].map((star) => (
                    <Star key={star} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <span className="text-slate-400 text-sm">Building tools I wish I had</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Products Grid */}
      <section id="products" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Choose Your Path
            </h2>
            <p className="text-slate-400 text-lg">
              Each tool is designed to uplift a critical area of your life
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <Card 
                key={index}
                className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-all hover:-translate-y-2 overflow-hidden group"
              >
                <div className={`h-2 bg-gradient-to-r ${product.color}`} />
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${product.color} text-white flex items-center justify-center mb-5`}>
                    {product.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {product.name}
                  </h3>
                  <p className="text-indigo-400 font-medium mb-3 text-sm">{product.tagline}</p>
                  <p className="text-slate-400 mb-5 text-sm">{product.description}</p>
                  
                  <ul className="space-y-2 mb-6">
                    {product.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-300 text-xs">
                        <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {product.external ? (
                    <a href={product.link} target="_blank" rel="noopener noreferrer">
                      <Button className={`w-full bg-gradient-to-r ${product.color} hover:opacity-90 text-sm`}>
                        Try {product.name}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  ) : (
                    <Link to={product.link}>
                      <Button className={`w-full bg-gradient-to-r ${product.color} hover:opacity-90 text-sm`}>
                        Explore {product.name}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Beta Testers / Early Access Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-indigo-900/50 to-purple-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/20 rounded-full border border-indigo-500/30 mb-6">
            <Gift className="w-4 h-4 text-indigo-400" />
            <span className="text-indigo-300 text-sm font-medium">Limited Time Offer</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Become a Beta Tester
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Get <span className="text-indigo-400 font-bold">FREE early access</span> in exchange for your honest feedback
          </p>
          <div className="bg-slate-900/80 rounded-2xl p-8 max-w-md mx-auto border border-slate-700">
            <div className="space-y-4 text-left mb-6">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span className="text-slate-300">Free access to all tools</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span className="text-slate-300">Help shape the product</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span className="text-slate-300">Lifetime discount when we launch</span>
              </div>
            </div>
            <form onSubmit={handleBetaSignup} className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
              />
              <Button 
                type="submit" 
                className="bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Joining...' : 'Join Beta'}
              </Button>
            </form>
            <p className="text-slate-500 text-xs mt-4">
              No spam. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <UpliftShield size={60} color="#6366F1" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Why Uplift?
          </h2>
          <p className="text-xl text-slate-400 mb-12">
            Founded by Robert Elder III with one mission: <br />
            <span className="text-white font-medium">Make success accessible to everyone.</span>
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6">
              <div className="text-4xl font-bold text-indigo-400 mb-2">AI</div>
              <p className="text-slate-400">Powered</p>
              <p className="text-slate-500 text-sm mt-2">Advanced AI that actually helps</p>
            </div>
            <div className="p-6">
              <div className="text-4xl font-bold text-emerald-400 mb-2">Fair</div>
              <p className="text-slate-400">Pricing</p>
              <p className="text-slate-500 text-sm mt-2">One-time payments, no tricks</p>
            </div>
            <div className="p-6">
              <div className="text-4xl font-bold text-blue-400 mb-2">Real</div>
              <p className="text-slate-400">Results</p>
              <p className="text-slate-500 text-sm mt-2">Tools that actually work</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Rise?
          </h2>
          <p className="text-xl text-slate-400 mb-8">
            Join {subscriberCount > 0 ? `${subscriberCount}+ people` : 'others'} who are transforming their careers, businesses, and finances with Uplift.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-lg px-12 py-6">
              Start Your Uplift Journey
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <UpliftLogoWhite size="md" showText={true} className="mb-4" />
              <p className="text-slate-500 text-sm max-w-md">
                Uplift helps you rise in every area that matters. 
                Career success, business growth, and financial freedom - all from one trusted source.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Products</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/careerboost" className="text-slate-500 hover:text-white">CareerBoost</Link></li>
                <li><Link to="/bizplanai" className="text-slate-500 hover:text-white">BizPlanAI</Link></li>
                <li><Link to="/creditboost" className="text-slate-500 hover:text-white">CreditBoost</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms" className="text-slate-500 hover:text-white">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-slate-500 hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-slate-500 text-sm">
                  Founded by <span className="text-white font-medium">Robert Elder III</span>, CEO
                </p>
                <p className="text-slate-600 text-xs mt-1">
                  © 2024-2025 Uplift III / Robert Elder III. All Rights Reserved.
                </p>
              </div>
              <div className="flex items-center gap-4">
                {trustBadges.slice(0, 2).map((badge, index) => (
                  <div key={index} className="flex items-center gap-1 text-slate-500 text-xs">
                    {badge.icon}
                    <span>{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UpliftHome;
